import { engineShortName } from './engine-names.js';

/**
 * The engines behind a result, one chip each (HU-33, HU-26).
 *
 * They used to be one chip reading «gestures 2.0.3 · coherence 1.4.2 · +5 motores». The summary hid
 * which five, and it counted engines that never ran: the versions map names every engine of the
 * build that produced the result, not the ones that were asked for. Now each active engine is a
 * chip of its own, with its version, and an engine that did not run is not shown at all. Each chip
 * names its engine by its short label («Gestos 2.0.3», «Veracidad 1.4.2»), never by its identifier.
 */

export interface EngineVersionChip {
  key: string;
  label: string;
}

/** The two engines anybody compares go first; the rest alphabetically, so a set always reads the same. */
const FIRST = ['gestures', 'coherence'];

/** Exported so every list of engines, the changelog's included, reads in this one order. */
export function byReadingOrder(a: string, b: string): number {
  const rank = (name: string): number => (FIRST.includes(name) ? FIRST.indexOf(name) : FIRST.length);

  return rank(a) - rank(b) || a.localeCompare(b);
}

/**
 * A chip per active engine, with its version when there is one.
 *
 * `active` decides which engines are shown. When nothing says — a result from before the ledger
 * recorded engines — every engine with a version is taken as having run, which is the most that
 * can be said about it.
 */
export function engineVersionChips(
  versions: Record<string, string> | null | undefined,
  active: readonly string[] | null | undefined,
): EngineVersionChip[] {
  const known = versions ?? {};
  const names =
    active && active.length > 0
      ? [...new Set(active)]
      : Object.keys(known).filter((name) => known[name] != null);

  return names.sort(byReadingOrder).map((name) => ({
    key: name,
    label: known[name] ? `${engineShortName(name)} ${known[name]}` : engineShortName(name),
  }));
}

/**
 * Which engines a run counts as active: the ones it executed, or, before it has executed any, the
 * ones it asked for — a queued run is still going to run them.
 */
export function activeEngines(run: {
  enginesExecuted: readonly string[];
  enginesRequested: readonly string[];
}): string[] {
  return [...(run.enginesExecuted.length > 0 ? run.enginesExecuted : run.enginesRequested)];
}
